import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useCallback, useEffect, useMemo, useState } from "react";
import { foyerApi } from "../http/foyer";
import { describeError, useToast } from "../components/toast";
import type { ActionKey } from "../components/toast/ToastProvider";
import { translateKey, type Translate } from "../i18n/translate";
import { useFormat } from "../i18n/format";
import { tokens } from "../theme";
import { platformLabel } from "../utils/devicePresentation";
import { PageContainer, PageHeader } from "../components/PageHeader";
import {
  PermissionGate,
  companyPlace,
  useHomePlace,
  usePermission,
} from "../auth/access";
import type {
  DeviceGroup,
  DevicePlatform,
  FleetDevice,
  OtaKind,
  Rollout,
  RolloutAdoption,
  RolloutStatus,
} from "@shared/types/foyer";

function deviceLabel(d: FleetDevice): string {
  const where = [d.roomId, d.premiseName].filter(Boolean).join(" · ");
  return where || d.id;
}

const NO_VALUE = "-";
const APK_URL_PLACEHOLDER = "https://foyer.nenda.com/apk/foyer-0.3.0.apk";
const FIRMWARE_URL_PLACEHOLDER = "https://foyer.nenda.com/fw/abc123.upg";
const FIRMWARE_FILE_PLACEHOLDER = "swuimage.bem";
const NTP_PLACEHOLDER = "pool.ntp.org";

const PLATFORMS: DevicePlatform[] = [
  "web",
  "stb",
  "philips",
  "tizen",
  "webos",
  "ios",
];

const STATUS_STYLE: Record<RolloutStatus, { bg: string; fg: string }> = {
  draft: { bg: tokens.rowAlt, fg: tokens.inkMuted },
  canary: { bg: tokens.warningTint, fg: tokens.warning },
  rolling: { bg: tokens.warningTint, fg: tokens.warning },
  paused: { bg: tokens.rowAlt, fg: tokens.inkMuted },
  completed: { bg: tokens.successTint, fg: tokens.success },
  rolledBack: { bg: tokens.rowAlt, fg: tokens.inkMuted },
};

/** A rollout status as a tinted chip. */
function StatusChip({ status }: { status: RolloutStatus }) {
  const { t } = useTranslation("premises");
  const s = STATUS_STYLE[status];
  return (
    <Chip
      label={t(`otaPage.status.${status}`)}
      size="small"
      sx={{ bgcolor: s.bg, color: s.fg, fontWeight: "fontWeightSemibold" }}
    />
  );
}

type RolloutAction = "start" | "advance" | "pause" | "resume" | "rollback";

/** The state-machine actions available from a given status. */
function actionsFor(status: RolloutStatus): RolloutAction[] {
  switch (status) {
    case "draft":
      return ["start", "rollback"];
    case "canary":
      return ["advance", "pause", "rollback"];
    case "rolling":
      return ["pause", "rollback"];
    case "paused":
      return ["resume", "rollback"];
    default:
      return [];
  }
}

const KIND_KEY = {
  web_bundle: "otaPage.kinds.webBundle",
  apk: "otaPage.kinds.apk",
  firmware: "otaPage.kinds.firmware",
} as const satisfies Record<OtaKind, string>;

/** Firmware can't be un-flashed, so its "rollback" only halts further devices. */
function actionLabel(
  t: Translate,
  action: RolloutAction,
  rollout: Rollout
): string {
  if (action === "rollback" && rollout.kind === "firmware")
    return t("premises:otaPage.actions.abort");
  return translateKey(t, `premises:otaPage.actions.${action}`);
}

/** One-line description of what a rollout does - OTA target, or the bulk action. */
function rolloutSummary(t: Translate, r: Rollout): string {
  if (r.action === "reboot") return t("premises:otaPage.summary.reboot");
  if (r.action === "message") {
    const body = r.message?.body ?? "";
    return body
      ? t("premises:otaPage.summary.messageWithBody", { body })
      : t("premises:otaPage.summary.message");
  }
  const kind = r.kind
    ? translateKey(t, `premises:${KIND_KEY[r.kind]}`)
    : t("premises:otaPage.summary.update");
  return t("premises:otaPage.summary.ota", {
    kind,
    version: r.targetVersion ?? NO_VALUE,
  });
}

/** How a rollout's adoption reads: OTA converges on a version; bulk actions
 *  converge on the device acking the command. */
function adoptionSummary(t: Translate, r: Rollout, a: RolloutAdoption): string {
  const counts = {
    atTarget: a.atTarget,
    total: a.total,
    dispatched: a.dispatched,
  };
  if (r.action !== "ota") return t("premises:otaPage.adoption.acked", counts);
  return r.targetVersion
    ? t("premises:otaPage.adoption.onVersion", {
        ...counts,
        version: r.targetVersion,
      })
    : t("premises:otaPage.adoption.onTarget", counts);
}

function selectorSummary(t: Translate, r: Rollout, groupName?: string): string {
  const parts: string[] = [];
  if (r.groupId)
    parts.push(
      t("premises:otaPage.selector.group", {
        name: groupName ?? r.groupId,
      })
    );
  if (r.premiseId) parts.push(r.premiseId);
  if (r.platform) parts.push(platformLabel(r.platform));
  return parts.length
    ? parts.join(" · ")
    : t("premises:otaPage.selector.allDevices");
}

function RolloutCard({
  rollout,
  adoption,
  groupName,
  onAction,
}: {
  rollout: Rollout;
  adoption?: RolloutAdoption;
  groupName?: string;
  onAction: (id: string, action: RolloutAction) => void;
}) {
  const { t } = useTranslation(["premises", "common"]);
  const place = companyPlace(rollout.companyId);
  const canRunUpdates = usePermission(
    "software-updates",
    "administrate",
    place
  );
  const canRebootWaves = usePermission("screens", "edit", place);
  const canMessageWaves = usePermission("screens/messages", "edit", place);
  const mayAct = (a: string) =>
    a === "rollback" || rollout.action === "ota"
      ? canRunUpdates
      : canRebootWaves && canMessageWaves;
  const actions = actionsFor(rollout.status).filter(mayAct);
  const pct = adoption?.pct ?? 0;
  // APK waves only: devices that install but need an on-screen confirmation
  // (no `otaApkSilent`). Worth surfacing - those devices stall until someone
  // walks into the room, which otherwise just looks like stuck adoption.
  const promptCount =
    rollout.kind === "apk"
      ? (adoption?.devices.filter((d) => d.needsPrompt).length ?? 0)
      : 0;
  return (
    <Card sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
        <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {rollout.name}
        </Typography>
        <StatusChip status={rollout.status} />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {t("otaPage.summaryLine", {
          summary: rolloutSummary(t, rollout),
          selector: selectorSummary(t, rollout, groupName),
        })}
      </Typography>
      {promptCount > 0 && (
        <Typography variant="caption" color="warning.main" display="block">
          {t("otaPage.needsPrompt", {
            count: promptCount,
            total: adoption?.total ?? 0,
          })}
        </Typography>
      )}
      {rollout.ntpServer && (
        <Typography variant="caption" color="text.secondary">
          <Trans
            t={t}
            i18nKey="otaPage.ntpServerValue"
            values={{ server: rollout.ntpServer }}
            components={{ strong: <strong /> }}
          />
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t("otaPage.adoptionLabel")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {adoption ? adoptionSummary(t, rollout, adoption) : NO_VALUE}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
        {actions.map((a) => (
          <Button
            key={a}
            size="small"
            variant={a === "rollback" ? "text" : "outlined"}
            color={a === "rollback" ? "error" : "primary"}
            onClick={() => onAction(rollout.rolloutId, a)}
          >
            {actionLabel(t, a, rollout)}
          </Button>
        ))}
        {actions.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            {rollout.status === "completed"
              ? rollout.action === "ota"
                ? t("otaPage.completedOta")
                : t("otaPage.completedAction")
              : t("otaPage.noFurtherActions")}
          </Typography>
        )}
      </Stack>
    </Card>
  );
}

interface DraftForm {
  name: string;
  kind: OtaKind;
  targetVersion: string;
  premiseId: string;
  platform: DevicePlatform | "";
  groupId: string;
  canaryPct: number;
  artifactUrl: string;
  fileName: string;
  sizeBytes: string;
  ntpServer: string;
}

const EMPTY_DRAFT: DraftForm = {
  name: "",
  kind: "web_bundle",
  targetVersion: "",
  premiseId: "",
  platform: "",
  groupId: "",
  canaryPct: 10,
  artifactUrl: "",
  fileName: "",
  sizeBytes: "",
  ntpServer: "",
};

/** What a finished rollout action reads as in a toast. */
const ACTION_DONE = {
  start: "otaPage.done.start",
  advance: "otaPage.done.advance",
  pause: "otaPage.done.pause",
  resume: "otaPage.done.resume",
  rollback: "otaPage.done.rollback",
} as const satisfies Record<RolloutAction, string>;

const ACTION_FAILED: Record<RolloutAction, ActionKey> = {
  start: "startRollout",
  advance: "advanceRollout",
  pause: "pauseRollout",
  resume: "resumeRollout",
  rollback: "rollbackRollout",
};

export default function OtaPage() {
  const home = useHomePlace();
  const [rollouts, setRollouts] = useState<Rollout[]>([]);
  const [adoption, setAdoption] = useState<Record<string, RolloutAdoption>>({});
  const [groups, setGroups] = useState<DeviceGroup[]>([]);
  const [devices, setDevices] = useState<FleetDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const fmt = useFormat();
  // Only a failed LOAD shows inline; every action reports through a toast.
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<DraftForm>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [uploadingFw, setUploadingFw] = useState(false);

  // Upload an OEM firmware image: hosts it at /fw/<id>.<ext> and auto-fills the
  // rollout's URL / file name / size from the one upload.
  const onFirmwareFile = useCallback(
    async (file: File | undefined | null) => {
      if (!file) return;
      setUploadingFw(true);
      try {
        const fw = await foyerApi.uploadFirmware(file);
        setDraft((d) => ({
          ...d,
          artifactUrl: fw.url,
          fileName: fw.originalName,
          sizeBytes: String(fw.size),
        }));
        toast.success(
          t("otaPage.firmwareUploaded", {
            name: fw.originalName,
            size: fmt.number(fw.size / 1048576, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            }),
          })
        );
      } catch (err) {
        toast.error(
          describeError(t, "uploadFirmware", err, { name: file.name })
        );
      } finally {
        setUploadingFw(false);
      }
    },
    [fmt, t, toast]
  );

  useEffect(() => {
    foyerApi
      .listDeviceGroups()
      .then((g) => setGroups(g.groups))
      .catch(() => {});
    foyerApi
      .fleetDevices()
      .then((f) => setDevices(f.devices))
      .catch(() => {});
  }, []);

  // Member preview for the currently-picked group in the create dialog.
  const pickedGroup = useMemo(
    () => groups.find((g) => g.groupId === draft.groupId),
    [groups, draft.groupId]
  );
  const groupPreview = useMemo(() => {
    if (!pickedGroup) return null;
    const byId = new Map(devices.map((d) => [d.id, d]));
    const members = pickedGroup.screenIds.map((id) => byId.get(id));
    const known = members.filter((d): d is FleetDevice => Boolean(d));
    return {
      total: pickedGroup.screenIds.length,
      online: known.filter((d) => d.online).length,
      labels: known.map(deviceLabel),
    };
  }, [pickedGroup, devices]);

  // How the fleet would take an APK wave today, from the last heartbeat's
  // reported capabilities: silent (unattended) / prompted (needs a human) /
  // no native installer at all (excluded from APK cohorts server-side).
  const apkCapability = useMemo(() => {
    let silent = 0;
    let prompt = 0;
    let unsupported = 0;
    for (const d of devices) {
      const caps = d.live?.capabilities ?? [];
      if (!caps.includes("otaApk")) unsupported += 1;
      else if (caps.includes("otaApkSilent")) silent += 1;
      else prompt += 1;
    }
    return { silent, prompt, unsupported };
  }, [devices]);

  /**
   * Devices whose native container we CANNOT push an update to (TIZEN-13).
   *
   * A Samsung panel updates its widget by re-reading `sssp_config.xml` and
   * deciding for itself - there is no command we can send and no cohort we can
   * hold back, so every panel pointed at that file takes the update at once.
   * A canary slider over those devices is a promise of control the platform
   * does not give us, and the honest fix is to SAY so rather than to model
   * per-cohort config URLs we would then have to maintain.
   *
   * Listed with the widget version each one is actually on, because that is the
   * question a canary was standing in for: "which of my panels is stale?"
   */
  const platformAppDevices = useMemo(
    () =>
      devices.filter((d) => d.live?.capabilities?.includes("otaPlatformApp")),
    [devices]
  );
  const platformAppVersions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of platformAppDevices) {
      const v = d.live?.appVersion || t("otaPage.unknownVersion");
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [platformAppDevices, t]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { rollouts: rs } = await foyerApi.listRollouts();
      setRollouts(rs);
      // Adoption comes from the detail endpoint (cohort ⋈ reported version).
      const details = await Promise.all(
        rs.map((r) =>
          foyerApi
            .rollout(r.rolloutId)
            .then((d) => [r.rolloutId, d.adoption] as const)
            .catch(() => null)
        )
      );
      const next: Record<string, RolloutAdoption> = {};
      for (const d of details) if (d) next[d[0]] = d[1];
      setAdoption(next);
    } catch {
      setError(t("otaPage.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onAction = useCallback(
    async (id: string, action: RolloutAction) => {
      const name = rollouts.find((r) => r.rolloutId === id)?.name ?? id;
      try {
        await foyerApi.rolloutAction(id, action);
        toast.success(t(ACTION_DONE[action], { name }));
        await load();
      } catch (err) {
        toast.error(describeError(t, ACTION_FAILED[action], err, { name }));
      }
    },
    [load, rollouts, t, toast]
  );

  const needsArtifact = draft.kind === "apk" || draft.kind === "firmware";
  const canCreate = useMemo(
    () =>
      draft.name.trim() !== "" &&
      draft.targetVersion.trim() !== "" &&
      (!needsArtifact || draft.artifactUrl.trim() !== ""),
    [draft, needsArtifact]
  );

  const create = useCallback(async () => {
    setSaving(true);
    try {
      await foyerApi.createRollout({
        name: draft.name.trim(),
        kind: draft.kind,
        targetVersion: draft.targetVersion.trim(),
        premiseId: draft.premiseId.trim() || undefined,
        platform: draft.platform || undefined,
        groupId: draft.groupId || undefined,
        canaryPct: draft.canaryPct,
        artifactUrl:
          draft.kind === "apk" || draft.kind === "firmware"
            ? draft.artifactUrl.trim()
            : undefined,
        fileName:
          draft.kind === "firmware" && draft.fileName.trim()
            ? draft.fileName.trim()
            : undefined,
        sizeBytes:
          draft.kind === "firmware" && draft.sizeBytes.trim()
            ? Number(draft.sizeBytes) || undefined
            : undefined,
        ntpServer: draft.ntpServer.trim() || undefined,
      });
      toast.success(t("otaPage.created", { name: draft.name.trim() }));
      setDialogOpen(false);
      setDraft(EMPTY_DRAFT);
      await load();
    } catch (err) {
      // The dialog stays open with what was typed.
      toast.error(
        describeError(t, "createRollout", err, { name: draft.name.trim() })
      );
    } finally {
      setSaving(false);
    }
  }, [draft, load, t, toast]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("otaPage.eyebrow")}
        title={t("common:labels.otaRollouts")}
        actions={
          <PermissionGate
            resource="software-updates"
            level="administrate"
            place={home}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              {t("otaPage.newRollout")}
            </Button>
          </PermissionGate>
        }
      />

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ mt: 3 }}>
        {loading ? (
          <Typography color="text.secondary">
            {t("common:states.loading")}
          </Typography>
        ) : rollouts.length === 0 ? (
          <Card
            sx={{
              p: 5,
              textAlign: "center",
              color: tokens.inkMuted,
            }}
          >
            <RocketLaunchOutlinedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography sx={{ mt: 1 }}>{t("otaPage.empty")}</Typography>
            <Typography variant="body2">{t("otaPage.emptyHint")}</Typography>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {rollouts.map((r) => (
              <Grid item key={r.rolloutId} xs={12} md={6}>
                <RolloutCard
                  rollout={r}
                  adoption={adoption[r.rolloutId]}
                  groupName={groups.find((g) => g.groupId === r.groupId)?.name}
                  onAction={onAction}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("otaPage.dialogTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label={t("common:labels.name")}
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              fullWidth
              autoFocus
            />
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>{t("otaPage.fields.kind")}</InputLabel>
                <Select
                  label={t("otaPage.fields.kind")}
                  value={draft.kind}
                  onChange={(e) =>
                    setDraft({ ...draft, kind: e.target.value as OtaKind })
                  }
                >
                  <MenuItem value="web_bundle">
                    {t("otaPage.kindOptions.webBundle")}
                  </MenuItem>
                  <MenuItem value="apk">
                    {t("otaPage.kindOptions.apk")}
                  </MenuItem>
                  <MenuItem value="firmware">
                    {t("otaPage.kindOptions.firmware")}
                  </MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={t("otaPage.fields.targetVersion")}
                value={draft.targetVersion}
                onChange={(e) =>
                  setDraft({ ...draft, targetVersion: e.target.value })
                }
                placeholder="0.3.0"
                fullWidth
              />
            </Stack>

            {draft.kind === "apk" && (
              <>
                <TextField
                  label={t("otaPage.fields.apkUrl")}
                  value={draft.artifactUrl}
                  onChange={(e) =>
                    setDraft({ ...draft, artifactUrl: e.target.value })
                  }
                  placeholder={APK_URL_PLACEHOLDER}
                  fullWidth
                />
                <Typography variant="caption" color="text.secondary">
                  <Trans
                    t={t}
                    i18nKey="otaPage.apkHint"
                    components={{ strong: <strong /> }}
                  />
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <Trans
                    t={t}
                    i18nKey="otaPage.apkFleet"
                    values={apkCapability}
                    components={{ strong: <strong /> }}
                  />
                </Typography>
              </>
            )}

            {draft.kind === "firmware" && (
              <>
                <Typography variant="caption" color="text.secondary">
                  {t("otaPage.firmwareHint")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <PermissionGate
                    resource="software-updates"
                    level="administrate"
                    place="nenda"
                  >
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadFileIcon />}
                      disabled={uploadingFw}
                    >
                      {uploadingFw
                        ? t("otaPage.uploading")
                        : t("otaPage.uploadFirmware")}
                      <input
                        type="file"
                        hidden
                        onChange={(e) => {
                          void onFirmwareFile(e.target.files?.[0]);
                          // Allow re-selecting the same file later.
                          e.target.value = "";
                        }}
                      />
                    </Button>
                  </PermissionGate>
                  <Typography variant="caption" color="text.secondary">
                    {t("otaPage.uploadHint")}
                  </Typography>
                </Stack>
                {uploadingFw && <LinearProgress />}
                <TextField
                  label={t("otaPage.fields.firmwareUrl")}
                  value={draft.artifactUrl}
                  onChange={(e) =>
                    setDraft({ ...draft, artifactUrl: e.target.value })
                  }
                  placeholder={FIRMWARE_URL_PLACEHOLDER}
                  fullWidth
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    label={t("otaPage.fields.fileName")}
                    placeholder={FIRMWARE_FILE_PLACEHOLDER}
                    value={draft.fileName}
                    onChange={(e) =>
                      setDraft({ ...draft, fileName: e.target.value })
                    }
                    fullWidth
                  />
                  <TextField
                    label={t("otaPage.fields.sizeBytes")}
                    type="number"
                    value={draft.sizeBytes}
                    onChange={(e) =>
                      setDraft({ ...draft, sizeBytes: e.target.value })
                    }
                    fullWidth
                  />
                </Stack>
              </>
            )}

            <Typography variant="caption" color="text.secondary">
              {t("otaPage.cohortHint")}
            </Typography>
            {groups.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>{t("otaPage.fields.deviceGroup")}</InputLabel>
                <Select
                  label={t("otaPage.fields.deviceGroup")}
                  value={draft.groupId}
                  onChange={(e) =>
                    // A group is a fixed cohort, so it overrides the
                    // premise/platform filters - clear them when one is picked.
                    setDraft({
                      ...draft,
                      groupId: e.target.value,
                      ...(e.target.value
                        ? { premiseId: "", platform: "" }
                        : {}),
                    })
                  }
                >
                  <MenuItem value="">{t("otaPage.noGroup")}</MenuItem>
                  {groups.map((g) => (
                    <MenuItem key={g.groupId} value={g.groupId}>
                      {g.name} ({g.screenIds.length})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {groupPreview && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: tokens.brandTintSoft,
                  border: `1px solid ${tokens.brandTint}`,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  <Trans
                    t={t}
                    i18nKey={
                      devices.length > 0
                        ? "otaPage.targetingWithOnline"
                        : "otaPage.targeting"
                    }
                    count={groupPreview.total}
                    values={{ online: groupPreview.online }}
                    components={{ strong: <strong /> }}
                  />
                </Typography>
                {groupPreview.labels.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ flexWrap: "wrap", gap: 0.75, mt: 1 }}
                  >
                    {groupPreview.labels.slice(0, 6).map((label, i) => (
                      <Chip
                        key={i}
                        size="small"
                        variant="outlined"
                        label={label}
                        sx={{ maxWidth: 220 }}
                      />
                    ))}
                    {groupPreview.labels.length > 6 && (
                      <Chip
                        size="small"
                        label={t("otaPage.moreDevices", {
                          count: groupPreview.labels.length - 6,
                        })}
                        sx={{ bgcolor: tokens.rowAlt, color: tokens.inkMuted }}
                      />
                    )}
                  </Stack>
                )}
              </Box>
            )}

            {draft.groupId ? (
              <Typography variant="caption" color="text.secondary">
                {t("otaPage.filtersIgnored")}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={2}>
              <TextField
                label={t("otaPage.fields.premiseSlug")}
                value={draft.premiseId}
                disabled={!!draft.groupId}
                onChange={(e) =>
                  setDraft({ ...draft, premiseId: e.target.value })
                }
                fullWidth
              />
              <FormControl fullWidth disabled={!!draft.groupId}>
                <InputLabel>{t("otaPage.fields.platform")}</InputLabel>
                <Select
                  label={t("otaPage.fields.platform")}
                  value={draft.platform}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      platform: e.target.value as DevicePlatform | "",
                    })
                  }
                >
                  <MenuItem value="">{t("otaPage.anyPlatform")}</MenuItem>
                  {PLATFORMS.map((p) => (
                    <MenuItem key={p} value={p}>
                      {platformLabel(p)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              <Trans
                t={t}
                i18nKey="otaPage.canaryHint"
                components={{ em: <em /> }}
              />
            </Typography>
            <TextField
              label={t("otaPage.fields.canaryPct")}
              type="number"
              value={draft.canaryPct}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  canaryPct: Number(e.target.value) || 0,
                })
              }
              inputProps={{ min: 1, max: 100 }}
              sx={{ width: 200 }}
            />

            {/* The canary cannot hold back a widget update, so say which
                devices it will not apply to and answer the question it was
                standing in for instead. See platformAppDevices. */}
            {platformAppDevices.length > 0 && (
              <Typography
                variant="caption"
                color="warning.main"
                data-testid="ota-platform-app-note"
              >
                <Trans
                  t={t}
                  i18nKey="otaPage.platformAppNote"
                  count={platformAppDevices.length}
                  values={{
                    versions: platformAppVersions
                      .map(([version, count]) =>
                        t("otaPage.versionCount", {
                          version,
                          devices: count,
                        })
                      )
                      .join(" · "),
                  }}
                  components={{ strong: <strong />, code: <code /> }}
                />
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary">
              {t("otaPage.ntpHint")}
            </Typography>
            <TextField
              label={t("otaPage.fields.ntpServer")}
              value={draft.ntpServer}
              onChange={(e) =>
                setDraft({ ...draft, ntpServer: e.target.value })
              }
              placeholder={NTP_PLACEHOLDER}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t("common:buttons.cancel")}
          </Button>
          <PermissionGate
            resource="software-updates"
            level="administrate"
            place={home}
            deniedProps={{ disabled: true }}
          >
            <Button
              variant="contained"
              disabled={!canCreate || saving}
              onClick={create}
            >
              {t("common:buttons.create")}
            </Button>
          </PermissionGate>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
